/**
 * AnalyticsErrorHandler — the app's global Angular ErrorHandler (registered in
 * app.config.ts, as in wip-map, where it was ported from).
 *
 * Contract:
 *   1. ALWAYS console.error FIRST, so nothing below can swallow the error.
 *   2. A stale chunk (a tab older than the last deploy asking for a lazy chunk
 *      the server no longer has) reloads once to the new build, quietly: no
 *      capture, no message (core/utils/chunk-reload.ts). If it happens again
 *      straight after that reload, it is captured and a plain toast says part
 *      of the page did not load.
 *   3. Everything else is captured as `error_occurred` plus PostHog's
 *      `$exception` (a no-op before PostHog is initialised; local dev hosts
 *      drop both in PosthogService).
 *
 * Stack hashes use a small DJB2 fold so PostHog can group similar errors
 * across users without leaking the full stack as a property.
 *
 * Collaborators are resolved lazily through the Injector: Angular builds the
 * ErrorHandler early, and an error during boot must still be reported even if
 * the toast stack or translations are not ready.
 */
import { ErrorHandler, Injectable, Injector, inject } from '@angular/core';
import { PaperToastService } from '@ricoh-paper/angular';
import { TranslocoService } from '@jsverse/transloco';
import { AnalyticsService } from './analytics.service';
import { ANALYTICS_EVENTS } from '../analytics/events';
import { ChunkReloadRecovery, findChunkLoadError } from '../utils/chunk-reload';
import { translateOr } from '../utils/translate-or';

/** The toast after a reload for a stale chunk did not help. Paper derives its test id: `paper-toast-<id>`. */
export const STALE_CHUNK_TOAST = {
  id: 'settings.app.stale-chunk',
  titleKey: 'shell.stale_chunk.title',
  bodyKey: 'shell.stale_chunk.body',
  title: 'Part of this page didn’t load',
  body: 'Settings couldn’t download part of this page, even after reloading. Check your connection, then refresh the page.',
} as const;

@Injectable({ providedIn: 'root' })
export class AnalyticsErrorHandler implements ErrorHandler {
  private readonly injector = inject(Injector);
  /**
   * Stale-chunk failures already dealt with. The router hands one failure over
   * twice (its navigation error handler, then the navigation's rejection via
   * RouterLink), and it must be captured and told once.
   */
  private readonly handledChunkErrors = new WeakSet<object>();

  handleError(error: unknown): void {
    // (1) Log first — keeps console.error + the dev overlay, whatever happens below.
    console.error(error);

    // (2) A stale chunk: reload once to the new build; say so plainly if that didn't help.
    const chunkError = findChunkLoadError(error);
    if (chunkError !== null) {
      if (typeof chunkError === 'object') {
        if (this.handledChunkErrors.has(chunkError)) return;
        this.handledChunkErrors.add(chunkError);
      }
      let outcome: 'reloading' | 'gave-up' = 'gave-up';
      try {
        outcome = this.injector.get(ChunkReloadRecovery).recover();
      } catch (secondary) {
        console.error('[error-handler] chunk recovery failed', secondary);
      }
      if (outcome === 'reloading') return;
      this.capture(error);
      this.showStaleChunkToast();
      return;
    }

    // (3) Everything else: telemetry.
    this.capture(error);
  }

  private capture(error: unknown): void {
    try {
      const analytics = this.injector.get(AnalyticsService);
      const err = this.toError(error);
      const context = this.contextFromUrl();
      const errorType = this.classify(err);
      analytics.capture(ANALYTICS_EVENTS.ERROR_OCCURRED, {
        error_type: errorType,
        error_context: context,
        error_message: this.truncate(err.message, 500),
        error_stack_hash: this.hashStack(err.stack ?? err.message),
      });
      // Also emit PostHog's canonical $exception event so the Error
      // Tracking product activates (auto-groups by stack hash, exposes a
      // dedicated Issues view, links to session replay). The typed
      // ERROR_OCCURRED event above stays — it's the analytics-contract
      // version of the same signal and keeps the typed query surface.
      analytics.captureException(err, {
        error_type: errorType,
        error_context: context,
      });
    } catch (secondary) {
      // Never let analytics break error reporting itself.
      console.error('[error-handler] analytics capture failed', secondary);
    }
  }

  private showStaleChunkToast(): void {
    try {
      const t = (key: string, fallback: string) => translateOr(this.translate(key), key, fallback);
      this.injector.get(PaperToastService).show({
        id: STALE_CHUNK_TOAST.id,
        title: t(STALE_CHUNK_TOAST.titleKey, STALE_CHUNK_TOAST.title),
        body: t(STALE_CHUNK_TOAST.bodyKey, STALE_CHUNK_TOAST.body),
        severity: 'error',
        priority: 'high',
      });
    } catch (secondary) {
      // The toast stack may not be mounted yet (an early-boot error).
      console.error('[error-handler] could not show the stale-chunk message', secondary);
    }
  }

  /** The catalogue's string, or null when Transloco can't answer (not configured, not loaded): the caller falls back to English. */
  private translate(key: string): string | null {
    try {
      return this.injector.get(TranslocoService).translate(key);
    } catch {
      // Transloco isn't usable yet (an early-boot error): English it is.
      return null;
    }
  }

  private toError(error: unknown): Error {
    if (error instanceof Error) return error;
    if (typeof error === 'string') return new Error(error);
    try {
      return new Error(JSON.stringify(error));
    } catch {
      return new Error(String(error));
    }
  }

  private classify(err: Error): 'graphql' | 'network' | 'validation' | 'timeout' | 'uncaught' {
    const msg = err.message?.toLowerCase() ?? '';
    if (msg.includes('graphql') || err.name === 'CombinedGraphQLErrors') return 'graphql';
    if (msg.includes('network') || msg.includes('fetch')) return 'network';
    if (msg.includes('timeout') || msg.includes('timed out')) return 'timeout';
    if (msg.includes('validation') || msg.includes('invalid')) return 'validation';
    return 'uncaught';
  }

  private contextFromUrl(): string {
    try {
      const hash = window.location.hash || '';
      const path = hash.replace(/^#/, '') || window.location.pathname;
      return path.split('?')[0] || 'unknown';
    } catch {
      return 'unknown';
    }
  }

  private truncate(s: string, max: number): string {
    return s.length > max ? s.slice(0, max) + '…' : s;
  }

  /** Short DJB2 hash of the stack — enough to group similar errors in PostHog. */
  private hashStack(stack: string): string {
    let h = 5381;
    for (let i = 0; i < stack.length; i++) {
      h = ((h << 5) + h + stack.charCodeAt(i)) | 0;
    }
    return (h >>> 0).toString(16).padStart(8, '0').slice(0, 12);
  }
}
